import { API_URL } from "../../config";
import type {
  ImportItem,
  MakeupFilters,
  MakeupListResponse,
  MakeupRecord,
  MakeupSummary,
} from "./types";

// Same pattern as ClassBackupImport/api.ts — a plain fetch client keeps this
// admin surface's network layer independent of the shared RTK Query slice.
async function request<T>(
  method: "GET" | "POST",
  path: string,
  body?: Record<string, unknown>
): Promise<T> {
  const url = `${API_URL}/class-backup-import/makeups${path}`;
  const res = await fetch(url, {
    method,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err: any = new Error(json?.error || json?.message || `HTTP ${res.status}`);
    err.status = res.status;
    err.body = json;
    throw err;
  }
  return json as T;
}

const qs = (params: Record<string, any>) => {
  const p = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    if (typeof v === "boolean") p.set(k, v ? "1" : "0");
    else p.set(k, String(v));
  });
  const s = p.toString();
  return s ? `?${s}` : "";
};

export const classMakeupApi = {
  list: (filters: MakeupFilters & { page?: number; per_page?: number }) =>
    request<MakeupListResponse>("GET", `/${qs(filters)}`),

  summary: (filters: MakeupFilters) =>
    request<MakeupSummary>("GET", `/summary${qs(filters)}`),

  preview: (filters: MakeupFilters) =>
    request<{ items: MakeupRecord[]; total: number }>("POST", "/preview", filters),

  previewOne: (record_key: string, from_date?: string, to_date?: string) =>
    request<{ record: MakeupRecord }>("POST", "/preview-one", {
      record_key,
      from_date,
      to_date,
    }),

  importOne: (record_key: string, from_date?: string, to_date?: string) =>
    request<{ run_id: number; status: string; record_key: string; error?: string }>(
      "POST",
      "/import",
      { record_key, from_date, to_date }
    ),

  importSelected: (payload: {
    selection_mode: "selected" | "all_filtered";
    record_keys?: string[];
    excluded_keys?: string[];
    filters?: MakeupFilters;
    from_date?: string;
    to_date?: string;
  }) =>
    request<{ run_id: number | null; items: ImportItem[]; message?: string }>(
      "POST",
      "/import-selected",
      payload as any
    ),

  listRuns: () => request<{ items: any[] }>("GET", "/runs"),
  runLogs: (run_id: number) =>
    request<{ items: any[] }>("POST", "/run/logs", { run_id }),
};
